/**
 * Tron-themed canvas icons — pixel-art style, drawn at any size.
 * Used in renderer for crash markers, fire trails, etc.
 */

/**
 * Draw a little devil smiley at (cx, cy) with given size.
 * Round face with horns, slanted eyes, and a devilish grin.
 */
export function drawTaunt(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  color: string,
  alpha: number = 0.9,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.globalAlpha = alpha;

  const r = size / 2;
  const lw = Math.max(1.5, size * 0.07);

  // Horns
  ctx.strokeStyle = '#f44';
  ctx.lineWidth = lw;
  ctx.beginPath();
  ctx.moveTo(-r * 0.55, -r * 0.3);
  ctx.lineTo(-r * 0.7, -r * 0.95);
  ctx.lineTo(-r * 0.25, -r * 0.55);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(r * 0.55, -r * 0.3);
  ctx.lineTo(r * 0.7, -r * 0.95);
  ctx.lineTo(r * 0.25, -r * 0.55);
  ctx.stroke();

  // Face circle
  ctx.beginPath();
  ctx.arc(0, 0, r * 0.65, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.globalAlpha = alpha * 0.2;
  ctx.fill();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = lw;
  ctx.stroke();

  // Slanted eyes
  ctx.strokeStyle = color;
  ctx.lineWidth = lw * 1.2;
  // Left eye — slanted down-left
  ctx.beginPath();
  ctx.moveTo(-r * 0.35, -r * 0.25);
  ctx.lineTo(-r * 0.12, -r * 0.1);
  ctx.stroke();
  // Right eye — slanted down-right
  ctx.beginPath();
  ctx.moveTo(r * 0.35, -r * 0.25);
  ctx.lineTo(r * 0.12, -r * 0.1);
  ctx.stroke();

  // Devilish grin — wide and curved up at edges
  ctx.beginPath();
  ctx.moveTo(-r * 0.4, r * 0.15);
  ctx.quadraticCurveTo(-r * 0.2, r * 0.45, 0, r * 0.3);
  ctx.quadraticCurveTo(r * 0.2, r * 0.45, r * 0.4, r * 0.15);
  ctx.strokeStyle = color;
  ctx.lineWidth = lw;
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw a tron-themed skull and crossbones at (cx, cy) with given size.
 * Neon circuit-board aesthetic: angular skull, pixel eyes, crossed lightning bolts.
 */
export function drawSkull(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  color: string,
  alpha: number = 0.7,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.globalAlpha = alpha;

  const s = size / 2;

  // Skull outline — angular/hexagonal shape
  ctx.beginPath();
  ctx.moveTo(-s * 0.5, -s * 0.9);
  ctx.lineTo(s * 0.5, -s * 0.9);
  ctx.lineTo(s * 0.8, -s * 0.5);
  ctx.lineTo(s * 0.8, s * 0.1);
  ctx.lineTo(s * 0.5, s * 0.4);
  ctx.lineTo(s * 0.3, s * 0.4);
  ctx.lineTo(s * 0.3, s * 0.6);
  ctx.lineTo(s * 0.1, s * 0.6);
  ctx.lineTo(s * 0.1, s * 0.4);
  ctx.lineTo(-s * 0.1, s * 0.4);
  ctx.lineTo(-s * 0.1, s * 0.6);
  ctx.lineTo(-s * 0.3, s * 0.6);
  ctx.lineTo(-s * 0.3, s * 0.4);
  ctx.lineTo(-s * 0.5, s * 0.4);
  ctx.lineTo(-s * 0.8, s * 0.1);
  ctx.lineTo(-s * 0.8, -s * 0.5);
  ctx.closePath();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, size * 0.08);
  ctx.stroke();

  // Eyes — two small squares
  const eyeSize = s * 0.25;
  ctx.fillStyle = color;
  ctx.fillRect(-s * 0.45, -s * 0.45, eyeSize, eyeSize);
  ctx.fillRect(s * 0.2, -s * 0.45, eyeSize, eyeSize);

  // Crossbones — two diagonal lines beneath
  ctx.beginPath();
  ctx.moveTo(-s * 0.9, s * 0.5);
  ctx.lineTo(s * 0.9, s * 1.0);
  ctx.moveTo(s * 0.9, s * 0.5);
  ctx.lineTo(-s * 0.9, s * 1.0);
  ctx.lineWidth = Math.max(1, size * 0.1);
  ctx.stroke();

  // Bone ends — small dots at each crossbone tip
  const dotR = size * 0.06;
  for (const [bx, by] of [[-s * 0.9, s * 0.5], [s * 0.9, s * 1.0], [s * 0.9, s * 0.5], [-s * 0.9, s * 1.0]]) {
    ctx.beginPath();
    ctx.arc(bx, by, dotR, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();
}

/**
 * Draw a tron-themed fire icon at (cx, cy) with given size.
 * Neon flame with circuit-line aesthetic.
 */
export function drawFire(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  alpha: number = 0.8,
): void {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.globalAlpha = alpha;

  const s = size / 2;

  // Outer flame — angular, sharp
  ctx.beginPath();
  ctx.moveTo(0, -s * 1.0);
  ctx.lineTo(s * 0.3, -s * 0.5);
  ctx.lineTo(s * 0.7, -s * 0.3);
  ctx.lineTo(s * 0.5, s * 0.1);
  ctx.lineTo(s * 0.8, s * 0.4);
  ctx.lineTo(s * 0.4, s * 0.9);
  ctx.lineTo(0, s * 0.6);
  ctx.lineTo(-s * 0.4, s * 0.9);
  ctx.lineTo(-s * 0.8, s * 0.4);
  ctx.lineTo(-s * 0.5, s * 0.1);
  ctx.lineTo(-s * 0.7, -s * 0.3);
  ctx.lineTo(-s * 0.3, -s * 0.5);
  ctx.closePath();
  ctx.fillStyle = '#f80';
  ctx.fill();
  ctx.strokeStyle = '#ff0';
  ctx.lineWidth = Math.max(1, size * 0.06);
  ctx.stroke();

  // Inner flame core — smaller, brighter
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.5);
  ctx.lineTo(s * 0.25, -s * 0.1);
  ctx.lineTo(s * 0.4, s * 0.3);
  ctx.lineTo(s * 0.15, s * 0.5);
  ctx.lineTo(0, s * 0.3);
  ctx.lineTo(-s * 0.15, s * 0.5);
  ctx.lineTo(-s * 0.4, s * 0.3);
  ctx.lineTo(-s * 0.25, -s * 0.1);
  ctx.closePath();
  ctx.fillStyle = '#ff0';
  ctx.fill();

  // Hot center dot
  ctx.beginPath();
  ctx.arc(0, s * 0.1, s * 0.12, 0, Math.PI * 2);
  ctx.fillStyle = '#fff';
  ctx.fill();

  ctx.restore();
}

/**
 * Draw a tron lightcycle on the canvas at (cx, cy) with given size.
 * Sleek angular cycle facing right with glowing trail.
 */
export function drawLightcycle(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
  color: string,
  alpha: number = 1.0,
  direction: string = 'RIGHT',
): void {
  ctx.save();
  ctx.translate(cx, cy);
  // Mirror horizontally for LEFT (so wheels stay at bottom), rotate for UP/DOWN
  if (direction === 'LEFT') {
    ctx.scale(-1, 1);
  } else if (direction === 'UP') {
    ctx.rotate(-Math.PI / 2);
  } else if (direction === 'DOWN') {
    ctx.rotate(Math.PI / 2);
  }
  ctx.globalAlpha = alpha;

  const s = size / 2;

  // Trail glow behind the cycle
  const trailGrad = ctx.createLinearGradient(-s * 1.8, 0, -s * 0.6, 0);
  trailGrad.addColorStop(0, 'transparent');
  trailGrad.addColorStop(1, color);
  ctx.strokeStyle = trailGrad;
  ctx.lineWidth = s * 0.15;
  ctx.beginPath();
  ctx.moveTo(-s * 1.8, s * 0.2);
  ctx.lineTo(-s * 0.6, s * 0.2);
  ctx.stroke();

  // Body — angular wedge shape
  ctx.beginPath();
  ctx.moveTo(s * 0.9, 0);        // nose
  ctx.lineTo(s * 0.3, -s * 0.5); // top front
  ctx.lineTo(-s * 0.5, -s * 0.5);// top back
  ctx.lineTo(-s * 0.7, -s * 0.2);// rear top
  ctx.lineTo(-s * 0.7, s * 0.2); // rear bottom
  ctx.lineTo(-s * 0.5, s * 0.4); // bottom back
  ctx.lineTo(s * 0.3, s * 0.4);  // bottom front
  ctx.closePath();
  // Solid dark fill so trail doesn't flash through
  ctx.fillStyle = '#0a0a0a';
  ctx.fill();
  // Color border
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, size * 0.06);
  ctx.stroke();

  // Windshield — bright line
  ctx.beginPath();
  ctx.moveTo(s * 0.5, -s * 0.2);
  ctx.lineTo(s * 0.2, -s * 0.45);
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(1, size * 0.05);
  ctx.stroke();

  // Front wheel
  ctx.beginPath();
  ctx.arc(s * 0.4, s * 0.4, s * 0.18, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, size * 0.06);
  ctx.stroke();

  // Rear wheel
  ctx.beginPath();
  ctx.arc(-s * 0.5, s * 0.4, s * 0.18, 0, Math.PI * 2);
  ctx.stroke();

  ctx.restore();
}

